import React, { useState } from 'react';
import { ClockIcon, CalendarIcon } from '../Icons';
import { WA_NUMBER, WA_DEFAULT_MSG } from '../../data/constants';
import { crearContactoWeb } from '../../utils/firestore';
import { waLink } from '../../utils/whatsapp';
import { gaEvent, adsLeadConversion } from '../../utils/analytics';
import './Agendar.css';

const AREA_NOMBRES: Record<string, string> = {
  laboral: 'Derecho Laboral',
  familia: 'Derecho de Familia',
  migratorio: 'Derecho Migratorio',
  civil: 'Derecho Civil',
  otro: 'Otro',
};

const SEND_EMAIL_URL = 'https://us-central1-souza-villa.cloudfunctions.net/sendConsultaEmail';

const BOOKING_LINKS: { nombre: string; link: string }[] = [
  { nombre: 'Valentina Villa', link: 'https://calendar.app.google/oGLaBkPMAQVM4sbNA' },
  { nombre: 'Gabriela Souza', link: 'https://calendar.app.google/3TR6pNeYQNkUZHpz9' },
];

const Agendar: React.FC = () => {
  const [formData, setFormData] = useState({
    nombre: '', email: '', telefono: '', area: '', modalidad: '', mensaje: '',
  });
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const resp = await fetch(SEND_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!resp.ok) throw new Error();
      setSuccess(true);
      // Conversión: envío exitoso del formulario de contacto
      gaEvent('generate_lead', {
        form: 'agendar_consulta',
        area: AREA_NOMBRES[formData.area] || formData.area || '(sin área)',
        modalidad: formData.modalidad || '(sin modalidad)',
      });
      adsLeadConversion('formulario');
      setFormData({ nombre: '', email: '', telefono: '', area: '', modalidad: '', mensaje: '' });
      crearContactoWeb({
        nombreCliente: formData.nombre,
        correo: formData.email,
        telefono: formData.telefono,
        tema: AREA_NOMBRES[formData.area] || formData.area,
        modalidad: formData.modalidad,
        mensaje: formData.mensaje,
        estadoWeb: 'Contacto',
        captadora: 'WEB',
        clienteWeb: true,
      }).catch(() => {});
    } catch {
      setError('Error al enviar. Por favor escríbanos por WhatsApp o al correo directamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="agendar" style={{ background: 'var(--dark-2)' }}>
      <div className="section-label">Agenda Online</div>
      <h2 className="section-title">Agende su<br /><em>consulta</em></h2>

      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.9, maxWidth: 520, marginTop: '0.5rem', marginBottom: '2rem' }}>
        Reserve una hora de atención con una de nuestras abogadas. Las consultas iniciales están disponibles en modalidad presencial y online.
      </p>

      <div className="agendar-note">
        <div className="agendar-note-icon"><ClockIcon /></div>
        <div className="agendar-note-text">
          <strong>Reserva directa en Google Calendar.</strong> Los horarios disponibles se actualizan en tiempo real desde la agenda de cada abogada. Seleccione su hora, confirme sus datos y recibirá la invitación por correo automáticamente.
        </div>
      </div>

      {/* Bloque de reserva destacado */}
      <div className="agendar-calendar-block">
        <div className="agendar-cal-header">
          <CalendarIcon />
          <span>Seleccione su abogada y reserve su hora</span>
        </div>
        <div className="agendar-cal-cards">
          {BOOKING_LINKS.map((ab) => (
            <a
              key={ab.nombre}
              href={ab.link}
              target="_blank"
              rel="noopener noreferrer"
              className="agendar-cal-card"
            >
              <div className="agendar-cal-initials">
                {ab.nombre.split(' ').map(p => p[0]).join('')}
              </div>
              <div className="agendar-cal-info">
                <div className="agendar-cal-name">{ab.nombre}</div>
                <div className="agendar-cal-sub">Ver disponibilidad →</div>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* 3 opciones */}
      <div className="agendar-options">
        <div className="agenda-opt">
          <div className="agenda-opt-num">Opción 01</div>
          <div className="agenda-opt-title">WhatsApp Directo</div>
          <div className="agenda-opt-desc">Escriba directamente a nuestro número de WhatsApp y un asistente coordinará su hora en minutos. Respuesta garantizada en horario hábil.</div>
          <a
            href={waLink(WA_DEFAULT_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary agenda-opt-btn"
          >
            Ir a WhatsApp →
          </a>
        </div>

        <div className="agenda-opt">
          <div className="agenda-opt-num">Opción 02</div>
          <div className="agenda-opt-title">Formulario Online</div>
          <div className="agenda-opt-desc">Complete el formulario de solicitud y nos comunicaremos para confirmar disponibilidad dentro de las próximas 2 horas hábiles.</div>
          <a href="#agendar-form" className="btn-primary agenda-opt-btn">
            Completar Formulario →
          </a>
        </div>

      </div>

      {/* Formulario */}
      <div id="agendar-form">
        {!success ? (
          <form className="agendar-form" onSubmit={handleSubmit}>
            <div className="form-title">Solicitar Consulta</div>

            <div className="form-group">
              <label className="form-label">Nombre completo</label>
              <input className="form-control" name="nombre" value={formData.nombre} onChange={handleChange} placeholder="Su nombre" required />
            </div>

            <div className="form-group">
              <label className="form-label">Correo electrónico</label>
              <input className="form-control" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="correo@ejemplo.com" required />
            </div>

            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-control" name="telefono" value={formData.telefono} onChange={handleChange} placeholder="+56 9 1234 5678" />
            </div>

            <div className="form-group">
              <label className="form-label">Área legal</label>
              <select className="form-control" name="area" value={formData.area} onChange={handleChange} required>
                <option value="">Seleccione un área</option>
                <option value="laboral">Derecho Laboral</option>
                <option value="familia">Derecho de Familia</option>
                <option value="migratorio">Derecho Migratorio</option>
                <option value="civil">Derecho Civil</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Modalidad</label>
              <select className="form-control" name="modalidad" value={formData.modalidad} onChange={handleChange}>
                <option value="">Seleccione modalidad</option>
                <option value="presencial-concepcion">Presencial Concepción</option>
                <option value="presencial-lomas">Presencial Lomas Coloradas</option>
                <option value="videollamada">Videollamada</option>
                <option value="telefonica">Telefónica</option>
              </select>
            </div>

            <div className="form-group full">
              <label className="form-label">Describa brevemente su situación</label>
              <textarea className="form-control" name="mensaje" value={formData.mensaje} onChange={handleChange} placeholder="Cuéntenos su situación..." />
            </div>

            <div className="form-actions">
              {error && <p style={{ color: '#c0504a', fontSize: '0.78rem', marginRight: 'auto' }}>{error}</p>}
              <button type="submit" className="btn-primary" disabled={sending}>
                {sending ? 'Enviando...' : 'Enviar Solicitud →'}
              </button>
            </div>
          </form>
        ) : (
          <div className="form-success visible">
            ✓ Su solicitud ha sido enviada con éxito. Nos comunicaremos a la brevedad.
          </div>
        )}
      </div>
    </section>
  );
};

export default Agendar;
